/**
 * `users/{uid}` y sus subcolecciones (sección 7).
 *
 * Decisión 13/10/2026 (aprobada): el documento raíz se mantiene; lo que en la 1.0 eran
 * campos sueltos pasa a:
 *   users/{uid}/addresses/{addressId}       N direcciones
 *   users/{uid}/fcmTokens/{token}           N dispositivos (token = id, sección 9)
 *   users/{uid}/private/proStatus           1 doc — nivel PRO, expiración, origen
 *   users/{uid}/private/taxProfile          1 doc — DAC7
 *   users/{uid}/private/traderProfile       1 doc — DSA (vendedor profesional)
 *   fcmTokenOwners/{token} = { uid }        índice inverso para el "robo de token"
 *
 * El tipo `User` es el modelo 2.0 y NO incluye los campos viejos; el script de migración
 * los lee con `LegacyUser` (al final del fichero) y los conserva 30 días (sección 17).
 * Todo lo que es dinero/privilegio lo escribe SOLO Functions (reglas: keeps()).
 */
import { z } from 'zod';
import {
  CountryCodeSchema,
  CurrencySchema,
  FcmTokenSchema,
  LanguageCodeSchema,
  TimestampSchema,
  UserIdSchema,
} from './primitives.js';
import { MAX_SIZES_PER_KIND, SIZE_PROFILE_KINDS } from '../search/savedSearch.js';
import { ExternalReputationSchema } from '../listings/import.js';

export const USER_ROLES = ['user', 'staff', 'admin'] as const;
export const UserRoleSchema = z.enum(USER_ROLES);
export type UserRole = z.infer<typeof UserRoleSchema>;

/** Campos que el PROPIO usuario puede escribir desde el cliente (reglas: todo lo demás, keeps). */
/** Tramos de lote (D5): a partir de 2, 3 y 5 artículos. */
export const SELLER_LEVELS = ['new', 'reliable', 'top', 'elite'] as const;
export type SellerLevel = (typeof SELLER_LEVELS)[number];
export const SellerStatsSchema = z.object({
  completed: z.number().int().min(0),
  cancelled_by_seller: z.number().int().min(0),
  expired_acceptance: z.number().int().min(0),
  expired_shipping: z.number().int().min(0),
});
export type SellerStats = z.infer<typeof SellerStatsSchema>;

export const BUNDLE_TIERS = ['2', '3', '5'] as const;
export type BundleTier = (typeof BUNDLE_TIERS)[number];

export const UserSelfEditableSchema = z.object({
  full_name: z.string().max(120).optional(),
  avatar_url: z.string().url().nullable().optional(),
  bio: z.string().max(1000).optional(),
  language: LanguageCodeSchema.optional(),
  currency: CurrencySchema.optional(),
  country_code: CountryCodeSchema.optional(),
  region: z.string().max(120).optional(),
  city: z.string().max(120).optional(),
  vacation_mode: z.boolean().optional(),
  /** Preferencias de notificación por tipo (sección 9, "política de notificaciones"). */
  notification_prefs: z
    .object({
      messages: z.boolean(),
      offers: z.boolean(),
      orders: z.boolean(),
      favorites_price_drop: z.boolean(),
      saved_searches: z.boolean(),
      marketing: z.boolean(),
      daily_digest: z.boolean(),
    })
    .partial()
    .optional(),
  /** D6 — mis tallas por tipo (varias por tipo: M y L) y mis marcas. */
  size_profile: z
    .record(z.enum(SIZE_PROFILE_KINDS), z.array(z.string().max(12)).max(MAX_SIZES_PER_KIND))
    .optional(),
  favorite_brands: z.array(z.string().max(60)).max(50).optional(),
  social_promo_opt_out: z.boolean().optional(),
  /**
   * Versión de los textos legales aceptada (`LEGAL_VERSION`) y cuándo; el detalle inmutable está en
   * `legalConsents/{uid}_{version}`. Sin ella (o con una anterior) la web pide aceptar (1.0
   * `terms_accepted` + `legal_consents`).
   */
  legal_accepted: z.object({ version: z.string().max(10), at: TimestampSchema }).optional(),
  /** Avisos emergentes (`settings/announcement.id_key`) ya cerrados; los últimos 20. */
  dismissed_announcements: z.array(z.string().max(60)).max(20).optional(),
  /**
   * D5 — descuento por lote por tramos (porcentaje entero por número mínimo de artículos del mismo
   * vendedor: `{ "2": 5, "3": 10, "5": 20 }`). La 1.0 tenía un único `bundle_discount_percent`.
   * Tope: `settings/fees.max_bundle_discount_percent`. Lo valida también el servidor al cotizar.
   */
  bundle_discounts: z.record(z.enum(BUNDLE_TIERS), z.number().int().min(0).max(100)).optional(),
});

/** Campos que SOLO escribe Functions (Admin SDK). */
export const UserServerOnlySchema = z.object({
  role: UserRoleSchema,
  email: z.string().email().nullable(),
  email_verified: z.boolean(),
  phone: z.string().nullable(),
  phone_verified: z.boolean(),
  kyc_verified: z.boolean(),
  /** Instantánea desnormalizada de private/proStatus para listados y reglas. */
  is_pro: z.boolean(),
  /** Instantánea de private/traderProfile.status === 'verified' (DSA). */
  is_trader: z.boolean(),
  /** Declarado por el vendedor (DSA art. 30 / derechos del consumidor): particular o profesional. */
  seller_type: z.enum(['private', 'trader']).optional(),
  /** Copia pública de los datos del profesional (lo escribe `users-saveTraderProfile`). */
  trader_public: z
    .object({
      legal_name: z.string(),
      trade_name: z.string().nullable(),
      address: z.string(),
      contact_email: z.string(),
      contact_phone: z.string().nullable(),
      vat_number: z.string().nullable(),
      registration_number: z.string().nullable(),
      returns_terms: z.string().nullable(),
      status: z.enum(['pending', 'verified', 'rejected', 'suspended']),
    })
    .nullable()
    .optional(),
  username: z.string().nullable(),
  username_normalized: z.string().nullable(),
  /** A2: perfiles de otras plataformas enlazados (solo Functions; se verifican por código en la bio). */
  external_reputation: z.array(ExternalReputationSchema).max(5).optional(),
  banned: z.boolean(),
  banned_until: TimestampSchema.nullable(),
  is_active: z.boolean(),
  messaging_restricted_until: TimestampSchema.nullable(),
  stripe_customer_id: z.string().nullable(),
  stripe_connect_id: z.string().nullable(),
  connect_charges_enabled: z.boolean(),
  connect_payouts_enabled: z.boolean(),
  connect_details_submitted: z.boolean(),
  /** Contadores desnormalizados (los maneja la máquina de pedidos). */
  total_sales: z.number().int().min(0),
  total_purchases: z.number().int().min(0),
  /** C3 — nivel de vendedor (lo recalcula `sellerLevel()` tras cada venta, penalización o valoración). */
  seller_level: z.enum(SELLER_LEVELS),
  /** Valoraciones recibidas (reviews.ts las mantiene al crear cada reseña, también las automáticas). */
  rating_avg: z.number().min(0).max(5).optional(),
  rating_count: z.number().int().min(0).optional(),
  /** Contadores del vendedor para el nivel (7b: penalizaciones por cancelar o dejar caducar). */
  seller_stats: SellerStatsSchema.optional(),
  /** C2 — Nuvvu Club: compras completadas (para la tarifa decreciente). */
  completed_purchases_count: z.number().int().min(0),
  referred_by: UserIdSchema.nullable(),
  created_at: TimestampSchema,
  updated_at: TimestampSchema,
  last_seen_at: TimestampSchema.nullable(),
  /** Marca del script de migración (sección 17); ausente en cuentas nacidas en la 2.0. */
  migrated_from_v1_at: TimestampSchema.optional(),
});

export const UserSchema = UserSelfEditableSchema.merge(UserServerOnlySchema);

/**
 * C3 — nivel de vendedor por ventas completadas, valoración media y tasa de fallos (ventas
 * canceladas por el vendedor o caducadas sin aceptar/enviar). Umbrales del plan (sección «C3»):
 * Nuevo → Fiable (5 ventas, ≥ 4,0, ≤ 10 % fallos) → Top (20, ≥ 4,5, ≤ 5 %) → Elite (50, ≥ 4,8, ≤ 2 %).
 */
export function sellerLevel(
  stats: Partial<SellerStats> | undefined,
  ratingAvg: number | undefined,
  ratingCount: number | undefined,
): SellerLevel {
  const completed = stats?.completed ?? 0;
  const failures =
    (stats?.cancelled_by_seller ?? 0) +
    (stats?.expired_acceptance ?? 0) +
    (stats?.expired_shipping ?? 0);
  const failRate = completed + failures === 0 ? 0 : failures / (completed + failures);
  // Sin valoraciones aún no se penaliza la media (cuenta como 5), pero sí hacen falta ventas.
  const rating = (ratingCount ?? 0) > 0 ? (ratingAvg ?? 0) : 5;
  if (completed >= 50 && rating >= 4.8 && failRate <= 0.02) return 'elite';
  if (completed >= 20 && rating >= 4.5 && failRate <= 0.05) return 'top';
  if (completed >= 5 && rating >= 4 && failRate <= 0.1) return 'reliable';
  return 'new';
}
export type User = z.infer<typeof UserSchema>;

export const userPath = (uid: string) => `users/${uid}` as const;

/**
 * D16 (13/10): nombre PÚBLICO de una cuenta. Solo el `username` (@usuario); el nombre real
 * (`full_name`) no sale nunca de users/{uid} hacia tarjetas, fichas, perfil, chat ni reseñas.
 * Sin username (cuentas antiguas), `user-` + los 6 últimos caracteres del uid.
 */
export function publicHandle(uid: string, username: string | null | undefined): string {
  const u = (username ?? '').trim();
  return u !== '' ? u : `user-${uid.slice(-6).toLowerCase()}`;
}

/** 1.0 reglas del @usuario: 3–20, letras sin tilde, números, punto, guion y guion bajo. */
export const USERNAME_RE = /^[a-zA-Z0-9._-]{3,20}$/;
/** Nombres que imitan a nuvvu o a su equipo (1.0 `auth.onboarding.usernameRules`). */
const USERNAME_RESERVED =
  /(nuvvu|admin|soporte|support|staff|official|oficial|moderador|moderator|help|ayuda)/i;

export type UsernameProblem = 'invalid' | 'reserved';

/** Valida y normaliza un @usuario; `null` si es válido. */
export function usernameProblem(raw: string): UsernameProblem | null {
  const u = raw.trim().replace(/^@/, '');
  if (!USERNAME_RE.test(u)) return 'invalid';
  if (USERNAME_RESERVED.test(u)) return 'reserved';
  return null;
}

/** Para buscar por handle: minúsculas, sin acentos, solo [a-z0-9._-]. */
export function normalizeHandle(handle: string): string {
  return handle
    .trim()
    .replace(/^@/, '')
    .normalize('NFKD')
    .replace(/[\u0300-\u036f]/g, '')
    .toLowerCase()
    .replace(/[^a-z0-9._-]/g, '');
}

// ---------- users/{uid}/addresses/{addressId} ----------

export const ADDRESS_TYPES = ['shipping', 'billing', 'pickup'] as const;

export const UserAddressSchema = z.object({
  type: z.enum(ADDRESS_TYPES),
  label: z.string().max(60).optional(),
  full_name: z.string().min(1).max(120),
  line1: z.string().min(1).max(200),
  line2: z.string().max(200).optional(),
  postal_code: z.string().min(1).max(20),
  city: z.string().min(1).max(120),
  region: z.string().max(120).optional(),
  /** Debe estar en settings/countries (sección 13b); lo valida la regla. */
  country_code: CountryCodeSchema,
  phone: z.string().max(30).optional(),
  is_default: z.boolean(),
  created_at: TimestampSchema,
  updated_at: TimestampSchema,
});
export type UserAddress = z.infer<typeof UserAddressSchema>;

export const userAddressesPath = (uid: string) => `users/${uid}/addresses` as const;

// ---------- users/{uid}/fcmTokens/{token} (sección 9) ----------

export const FcmTokenDocSchema = z.object({
  platform: z.enum(['web', 'android', 'ios']),
  app_version: z.string().max(40).nullable(),
  created_at: TimestampSchema,
  last_seen_at: TimestampSchema,
});
export type FcmTokenDoc = z.infer<typeof FcmTokenDocSchema>;

export const userFcmTokensPath = (uid: string) => `users/${uid}/fcmTokens` as const;

/** fcmTokenOwners/{token} = { uid } — índice inverso; solo Functions. */
export const FcmTokenOwnerSchema = z.object({
  uid: UserIdSchema,
  token: FcmTokenSchema,
  updated_at: TimestampSchema,
});
export type FcmTokenOwner = z.infer<typeof FcmTokenOwnerSchema>;
export const fcmTokenOwnerPath = (token: string) => `fcmTokenOwners/${token}` as const;

// ---------- users/{uid}/private/* (solo dueño y admin leen; solo Functions escribe) ----------

export const PRO_TIERS = ['basic', 'plus', 'business', 'elite'] as const;
export const ProTierSchema = z.enum(PRO_TIERS);
export type ProTier = z.infer<typeof ProTierSchema>;

/** Origen del PRO: Stripe (web), tiendas (RevenueCat) o concedido por admin. Igual que `pro_source` 1.0. */
export const PRO_SOURCES = ['stripe', 'app_store', 'play_store', 'admin_grant'] as const;
export const ProSourceSchema = z.enum(PRO_SOURCES);

export const ProStatusSchema = z.object({
  tier: ProTierSchema.nullable(),
  /** Las reglas validan por ESTADO (expires_at > now), no por presencia de campos (sección 3). */
  expires_at: TimestampSchema.nullable(),
  source: ProSourceSchema.nullable(),
  subscription_id: z.string().nullable(),
  granted_by: UserIdSchema.nullable(),
  granted_at: TimestampSchema.nullable(),
  updated_at: TimestampSchema,
});
export type ProStatus = z.infer<typeof ProStatusSchema>;

export const TaxProfileSchema = z.object({
  /** DAC7 (sección 15). Se pide al llegar a 25 ventas o 1.800 €/año. */
  legal_name: z.string().min(1).max(200),
  tax_id: z.string().min(1).max(40),
  tax_id_country: CountryCodeSchema,
  vat_number: z.string().max(40).nullable(),
  vat_vies_validated_at: TimestampSchema.nullable(),
  birth_date: z
    .string()
    .regex(/^\d{4}-\d{2}-\d{2}$/)
    .nullable(),
  registration_number: z.string().max(60).nullable().optional(),
  address_id: z.string().min(1),
  /** IBAN cifrado AES-256-GCM (como en la 1.0); nunca en claro. */
  iban_encrypted: z.string().nullable(),
  iban_last4: z.string().length(4).nullable(),
  status: z.enum(['missing', 'requested', 'provided', 'blocked']),
  reminders_sent: z.number().int().min(0),
  updated_at: TimestampSchema,
});
export type TaxProfile = z.infer<typeof TaxProfileSchema>;

export const TraderProfileSchema = z.object({
  /** DSA art. 30: datos del vendedor profesional, verificados antes de la primera venta PRO. */
  legal_name: z.string().min(1).max(200),
  trade_name: z.string().max(120).nullable().optional(),
  tax_id: z.string().max(40).nullable().optional(),
  registration_number: z.string().max(60).nullable(),
  vat_number: z.string().max(40).nullable(),
  /** Condiciones de desistimiento y devolución que ve el comprador. */
  returns_terms: z.string().max(2000).nullable().optional(),
  address_id: z.string().min(1),
  contact_email: z.string().email(),
  contact_phone: z.string().max(30).nullable(),
  identity_verification_session: z.string().nullable(),
  status: z.enum(['pending', 'verified', 'rejected', 'suspended']),
  verified_at: TimestampSchema.nullable(),
  updated_at: TimestampSchema,
});
export type TraderProfile = z.infer<typeof TraderProfileSchema>;

export const USER_PRIVATE_DOCS = ['proStatus', 'taxProfile', 'traderProfile'] as const;
export type UserPrivateDoc = (typeof USER_PRIVATE_DOCS)[number];
export const userPrivatePath = (uid: string, doc: UserPrivateDoc) =>
  `users/${uid}/private/${doc}` as const;

// ---------- Modelo viejo (solo para el script de migración) ----------

/**
 * Campos de `users` en la 1.0 que el script de migración lee para crear las subcolecciones.
 * Lista sacada de firestore.rules 1.0 (keeps(...)) y proPlans.js. Passthrough: no se valida
 * nada más que lo que se migra.
 */
export const LegacyUserSchema = z
  .object({
    wallet_balance: z.number().optional(),
    wallet_retained: z.number().optional(),
    wallet_pending_payout: z.number().optional(),
    is_pro: z.boolean().optional(),
    pro_tier: z.string().nullable().optional(),
    pro_expires_at: z.unknown().optional(),
    pro_source: z.string().nullable().optional(),
    pro_granted_by: z.string().nullable().optional(),
    pro_granted_at: z.unknown().optional(),
    stripe_subscription_id: z.string().nullable().optional(),
    stripe_customer_id: z.string().nullable().optional(),
    stripe_connect_id: z.string().nullable().optional(),
    connect_charges_enabled: z.boolean().optional(),
    connect_payouts_enabled: z.boolean().optional(),
    connect_details_submitted: z.boolean().optional(),
    kyc_verified: z.boolean().optional(),
    email_verified: z.boolean().optional(),
    phone_verified: z.boolean().optional(),
    created_date: z.string().optional(),
    username: z.string().nullable().optional(),
    username_normalized: z.string().nullable().optional(),
    referred_by: z.string().nullable().optional(),
    total_sales: z.number().optional(),
    total_purchases: z.number().optional(),
    banned: z.boolean().optional(),
    banned_until: z.unknown().optional(),
    is_active: z.boolean().optional(),
    messaging_restricted_until: z.unknown().optional(),
    currency: z.string().optional(),
    country_code: z.string().optional(),
    detected_country: z.string().optional(),
    detected_city: z.string().optional(),
    region: z.string().optional(),
    full_name: z.string().optional(),
    avatar_url: z.string().nullable().optional(),
    role: z.string().optional(),
  })
  .passthrough();
export type LegacyUser = z.infer<typeof LegacyUserSchema>;

/** `user_addresses` 1.0 (colección raíz) → users/{uid}/addresses. */
export const LegacyUserAddressSchema = z
  .object({ user_id: z.string(), type: z.string().optional(), is_default: z.boolean().optional() })
  .passthrough();

/** `device_tokens` 1.0 → users/{uid}/fcmTokens + fcmTokenOwners. */
export const LegacyDeviceTokenSchema = z
  .object({
    user_id: z.string(),
    token: z.string(),
    is_active: z.boolean().optional(),
    platform: z.string().optional(),
  })
  .passthrough();

/** `legalConsents/{uid}_{version}` — registro inmutable de la aceptación (1.0 `legal_consents`). */
export const LegalConsentSchema = z.object({
  user_id: UserIdSchema,
  version: z.string().min(1).max(10),
  /** Documentos aceptados en ese momento (terms, privacy, cookies…). */
  docs: z.array(z.string().max(20)).min(1).max(12),
  language: z.string().max(5),
  accepted_at: TimestampSchema,
});
export type LegalConsent = z.infer<typeof LegalConsentSchema>;
